import {useGSAP} from '@gsap/react'
import {Button} from '@repo/shared/Customer/Coop/Button/Button'
import {HowToPlayModal} from '@repo/shared/Customer/Coop/HowToPlayModal/HowToPlayModal'
import Spinner from '@repo/shared/Spinner'
import {toastMessage} from '@repo/shared/Toast'
import {debugLogger} from '@repo/shared/utils/debugLogger'
import clsx from 'clsx'
import gsap from 'gsap'
import {useAtomValue} from 'jotai'
import {FC, useEffect, useRef, useState} from 'react'
import {TransitionStatus} from 'react-transition-state'
import {Vector3} from 'three'
import {EScene} from '../../types/scene.types'
import {sceneState} from '../Store/atoms'
import {useGameCode} from './PreLobby.hooks'

import {useStartSinglePlayerTrigger} from '@/hooks/useStartSinglePlayerTrigger'
import {ContentWidthLimiter} from '@repo/shared/Customer/Coop/ContentWidthLimiter/ContentWidthLimiter'
import {useTracking} from '@repo/shared/Tracking/Tracking.hooks'
import FrameStore from '../Store/FrameStore'
import IllustrationPartyItem1 from './assets/party-item-1-illustration.svg'
import IllustrationPartyItem2 from './assets/party-item-2-illustration.svg'
import IllustrationPartyItem3 from './assets/party-item-3-illustration.svg'

export const lobbyPos = new Vector3(-162, 60, 0)
const fStore = new FrameStore()

interface IPreLobbyProps {
  transitionStatus: TransitionStatus
}

const PreLobby: FC<IPreLobbyProps> = ({transitionStatus}) => {
  const scene = useAtomValue(sceneState)

  const buttonsRef = useRef<HTMLDivElement>(null)
  const logoRef = useRef<HTMLImageElement>(null)
  const enterTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))
  const exitTimeline = useRef<gsap.core.Timeline>(gsap.timeline({paused: true}))
  const [disableButtons, setDisableButtons] = useState(false)

  const [howToPlayModalOpen, setHowToPlayModalOpen] = useState(false)

  const {startSinglePlayer} = useStartSinglePlayerTrigger()

  // Get the game code from the server
  const {getCode, error, isLoading} = useGameCode()
  const {trackEvent} = useTracking()

  // animate the lobby out of screen in pararell with the camera pan down
  useGSAP(
    () => {
      /**
       * Enter animation
       */
      enterTimeline.current.set(logoRef.current, {scale: 0, rotate: 3})
      enterTimeline.current.set(buttonsRef.current, {opacity: 0})

      enterTimeline.current.to(logoRef.current, {
        scale: 1,
        rotate: 0,
        duration: 1,
        delay: 0.5,
        ease: 'elastic.out(1)',
      })

      enterTimeline.current.to(
        buttonsRef.current,
        {
          opacity: 1,
          duration: 0.4,
          delay: 0.2,
          ease: 'power1.out',
        },
        '<',
      )

      /**
       * Exit timeline
       */

      exitTimeline.current.to(logoRef.current, {
        opacity: 0,
        duration: 0.3,
      })

      exitTimeline.current.to(
        buttonsRef.current,
        {
          opacity: 0,
          duration: 0.2,
          delay: 0.1,
        },
        '<',
      )
    },
    {
      dependencies: [scene],
    },
  )

  useEffect(() => {
    if (error) {
      toastMessage.error(error)
    }
  }, [error])

  const handleSinglePlayer = () => {
    trackEvent({
      domain: EScene.PRE_LOBBY,
      trigger: 'click',
      source: 'single_player_button',
    })

    setDisableButtons(true)
    startSinglePlayer()
  }

  /**
   * Enter and leave states
   */
  useEffect(() => {
    if (transitionStatus === 'entered') {
      debugLogger({
        message: 'Enter state',
        tag: 'Pre Lobby',
      })
      enterTimeline.current.play()
    } else if (transitionStatus === 'exiting') {
      debugLogger({
        message: 'Exit state',
        tag: 'Pre Lobby',
      })
      enterTimeline.current.pause()
      exitTimeline.current.play()
    }
  }, [transitionStatus])

  return (
    <ContentWidthLimiter id="pre-lobby">
      <div className="fixed left-0 top-0 z-30 flex h-full w-full flex-col items-center justify-center pb-[12vh] pt-[18vh] duration-1000">
        <div className="mx-[52px] mb-[48px] lg:mb-[64px]">
          <img ref={logoRef} className="" src="assets/logo.svg" />
        </div>

        <div className="items-between mx-[100px] flex max-w-[1000px] flex-col justify-center">
          <div ref={buttonsRef} className="relative mb-[24px] flex gap-[16px] lg:mb-[36px]">
            <div className="absolute -left-0 hidden lg:block">
              <div className="-translate-x-full pr-[20px] pt-[30px]">
                <img
                  src="assets/arrow-right.svg"
                  role="presentation"
                  className="absolute right-[24px] top-[48px]"
                />
                <div
                  id="win-text"
                  className="ml-[24px] w-[190px] -rotate-[15deg] text-center text-[22px] font-bold leading-normal text-white"
                >
                  Prøv å slå dagens tid!
                </div>
              </div>
            </div>

            <Button
              disabled={isLoading || disableButtons}
              id="single-game-btn"
              color="blue"
              className={clsx(transitionStatus === 'exiting' && '!pointer-events-none')}
              onClick={handleSinglePlayer}
              icon={<img src="/assets/trophy.svg" className="size-[24px] lg:size-auto" />}
            >
              Dagens sprint
            </Button>

            <Button
              disabled={isLoading || disableButtons}
              id="multiplayer-game-btn"
              color="purple"
              className={clsx(
                'hidden lg:flex',
                transitionStatus === 'exiting' && '!pointer-events-none',
              )}
              onClick={() => {
                trackEvent({
                  domain: EScene.PRE_LOBBY,
                  trigger: 'click',
                  source: 'multiplayer_button',
                })

                setHowToPlayModalOpen(true)
              }}
              icon={<img src="/assets/people.svg" />}
            >
              {isLoading ? <Spinner color="#fffff" size={28} /> : 'Flerspill'}
            </Button>
            <div className="absolute -right-0 hidden lg:block">
              <div className="translate-x-full pl-[20px]">
                <img
                  src="assets/arrow-left.svg"
                  role="presentation"
                  className="absolute left-[24px] top-[82px]"
                />
                <div
                  id="challenge-text"
                  className="ml-[24px] w-[190px] rotate-[15deg] text-center text-[22px] font-bold leading-tight text-white"
                >
                  Koble til storskjerm og utforde familien / vennegjengen
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <HowToPlayModal
        isOpen={howToPlayModalOpen}
        title="Det er like enkelt som 1-2-3!"
        onClose={() => {
          trackEvent({
            domain: EScene.PRE_LOBBY,
            trigger: 'click',
            source: 'how_to_play_modal_close_button',
          })

          setHowToPlayModalOpen(false)
        }}
        onConfirm={() => {
          trackEvent({
            domain: EScene.PRE_LOBBY,
            trigger: 'click',
            source: 'how_to_play_modal_start_button',
          })

          setHowToPlayModalOpen(false)
          getCode()
        }}
        items={[
          {
            illustration: IllustrationPartyItem1,
            title: 'Se på storskjermen',
            description:
              'Dette er spillbrettet. Alle må kunne se skjermen, enten på PC, nettbrett eller TV.',
            titleId: 'screen-step-title',
            descriptionId: 'screen-step-description',
          },
          {
            illustration: IllustrationPartyItem2,
            title: 'Bli med på mobilen',
            description:
              'Scan QR-koden med mobilen, og skriv inn spillkoden på neste side for å bli med.',
            titleId: 'mobile-step-title',
            descriptionId: 'mobile-step-description',
          },
          {
            illustration: IllustrationPartyItem3,
            title: 'Start spillet',
            description:
              'Den første som skriver inn koden er spillvert og starter spillet når alle er klare. Alle klikker så raskt de kan på hver sin mobil når spillet er i gang.',
            titleId: 'start-step-title',
            descriptionId: 'start-step-description',
          },
        ]}
      />
    </ContentWidthLimiter>
  )
}

export default PreLobby
